/* ==========================================================================
   Collection v2 : comportements du gabarit de collection (sections cv2-*).

   1. Textes repliés (description, texte SEO)
   2. En-tête : pastille active visible, taille gardée d'une collection à
      l'autre, lien du guide masqué si sa cible manque
   3. Barre collante calée sous l'en-tête du site
   4. Tiroir « Filtrer et trier » : mise à jour de la grille sans rechargement
      (Section Rendering API), historique du navigateur
   5. « Voir plus » : page suivante ajoutée à la suite ; au retour arrière,
      les lots déjà vus sont rechargés et la position retrouvée
   6. Ajout rapide : feuille rendue par la section cv2-ajout, panier UpCart
   7. Onglets de la bande packs, galeries des cartes (points, flèches),
      pastilles de couleur des cartes

   Sans JS, tout reste utilisable : le tiroir est un formulaire GET, « Voir
   plus » un lien vers la page suivante, le bouton d'ajout mène à la fiche.
   ========================================================================== */
(() => {
  'use strict';

  /* Chargé par plusieurs sections du gabarit : une seule exécution. */
  if (window.cv2Pret) return;
  window.cv2Pret = true;

  const q = (sel, root = document) => root.querySelector(sel);
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const lire = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* Réponses déjà reçues : un filtre retiré puis remis ne refait pas d'appel. */
  const cache = new Map();
  function charger(url) {
    if (!cache.has(url)) {
      cache.set(
        url,
        fetch(url, { credentials: 'same-origin' }).then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
      );
      cache.get(url).catch(() => cache.delete(url));
    }
    return cache.get(url);
  }

  /* Défilement de la page bloqué pendant qu'une feuille est ouverte. */
  function fige(oui) {
    document.documentElement.classList.toggle('cv2-fige', oui);
  }

  /* 1. Textes repliés ------------------------------------------------------ */

  function plis(root) {
    qa('[data-cv2-deplier]', root).forEach((btn) => {
      if (btn.dataset.cv2Pret) return;
      btn.dataset.cv2Pret = '1';
      const cible = document.getElementById(btn.getAttribute('aria-controls'));
      if (!cible) return;
      const plus = btn.dataset.plus || btn.textContent.trim();
      const moins = btn.dataset.moins || plus;

      if (cible.hasAttribute('data-cv2-cache')) {
        /* Description complète sous une intro courte : cachée, dépliable. */
        cible.hidden = true;
        btn.hidden = false;
        btn.addEventListener('click', () => {
          const ouvrir = cible.hidden;
          cible.hidden = !ouvrir;
          btn.setAttribute('aria-expanded', String(ouvrir));
          btn.textContent = ouvrir ? moins : plus;
        });
        return;
      }

      if (cible.hasAttribute('data-cv2-plie')) {
        /* Texte long : replié sur quelques lignes s'il déborde, sinon rien. */
        cible.classList.add('is-plie');
        if (cible.scrollHeight <= cible.clientHeight + 4) {
          cible.classList.remove('is-plie');
          return;
        }
        btn.hidden = false;
        btn.addEventListener('click', () => {
          const plie = cible.classList.toggle('is-plie');
          btn.setAttribute('aria-expanded', String(!plie));
          btn.textContent = plie ? plus : moins;
        });
      }
    });
  }

  /* 2. En-tête ------------------------------------------------------------- */

  const TAILLE = /^filter\.v\.option\.(taille|size|talla|gr(o|ö)(ss|ß)e|maat|taglia)/i;

  function entete(root) {
    qa('[data-cv2-raccourcis]', root).forEach((liste) => {
      if (liste.dataset.cv2Pret) return;
      liste.dataset.cv2Pret = '1';
      const actif = q('[aria-current="page"]', liste);
      if (!actif) return;
      const li = actif.parentElement;
      const marge = parseFloat(getComputedStyle(liste).paddingLeft) || 16;
      if (li.offsetLeft + li.offsetWidth > liste.clientWidth - marge) {
        liste.scrollLeft = li.offsetLeft - marge;
      }
    });

    /* D'une collection à sa voisine, la taille choisie suit (maquette 5). */
    qa('[data-cv2-garde]', root).forEach((a) => {
      if (a.dataset.cv2Pret) return;
      a.dataset.cv2Pret = '1';
      a.addEventListener('click', () => {
        const tailles = [];
        new URLSearchParams(location.search).forEach((v, k) => {
          if (TAILLE.test(k)) tailles.push([k, v]);
        });
        if (!tailles.length) return;
        const url = new URL(a.getAttribute('href'), location.origin);
        tailles.forEach(([k, v]) => url.searchParams.append(k, v));
        a.setAttribute('href', url.pathname + url.search);
      });
    });

    qa('[data-cv2-guide-lien]', root).forEach((a) => {
      const href = a.getAttribute('href') || '';
      if (href.charAt(0) === '#' && !document.getElementById(href.slice(1))) a.hidden = true;
    });
  }

  /* 3. Barre collante ------------------------------------------------------ */

  function barre(grille) {
    const b = q('[data-cv2-barre]', grille);
    if (!b) return;
    const header = document.getElementById('shopify-section-header');
    const regler = () => {
      let h = 0;
      if (
        header &&
        header.classList.contains('shopify-section-header-sticky') &&
        !header.classList.contains('shopify-section-header-hidden')
      ) {
        h = header.offsetHeight;
      }
      grille.style.setProperty('--cv2-haut', h + 'px');
    };
    if (header && 'MutationObserver' in window) {
      new MutationObserver(regler).observe(header, { attributes: true, attributeFilter: ['class'] });
    }
    regler();

    /* Ombre sous la barre une fois collée. */
    if ('IntersectionObserver' in window) {
      const repere = document.createElement('span');
      repere.setAttribute('aria-hidden', 'true');
      repere.style.cssText = 'display:block;height:1px;margin-bottom:-1px';
      b.before(repere);
      new IntersectionObserver((entrees) => {
        b.classList.toggle('is-collee', !entrees[0].isIntersecting && entrees[0].boundingClientRect.top < 0);
      }).observe(repere);
    }
  }

  /* 4. Filtres et tri -------------------------------------------------------- */

  /* Paramètres du formulaire, sans les champs vides ni le tri par défaut. */
  function paramsDu(form) {
    const tri = q('[data-cv2-defaut]', form);
    const defaut = tri ? tri.dataset.cv2Defaut : '';
    const p = new URLSearchParams();
    new FormData(form).forEach((v, k) => {
      if (typeof v !== 'string' || v.trim() === '') return;
      if (k === 'sort_by' && v === defaut) return;
      p.append(k, v);
    });
    return p;
  }

  function annoncer(grille, texte) {
    const zone = q('[data-cv2-annonce]', grille);
    if (!zone) return;
    zone.textContent = '';
    window.setTimeout(() => {
      zone.textContent = texte;
    }, 60);
  }

  function synchroTri(grille, params) {
    const bloc = q('[data-cv2-groupe="tri"]', grille);
    if (!bloc) return;
    const voulu = params.get('sort_by') || bloc.dataset.cv2Defaut || '';
    qa('input[name="sort_by"]', bloc).forEach((r) => {
      r.checked = r.value === voulu;
    });
  }

  /* Remplace la grille, la barre et le contenu du tiroir par ceux de la
     réponse, en gardant les accordéons ouverts et le focus clavier. */
  function remplacer(grille, doc) {
    const neuf = (sel) => q(sel, doc);

    const res = q('[data-cv2-resultats]', grille);
    const resN = neuf('[data-cv2-resultats]');
    if (res && resN) res.innerHTML = resN.innerHTML;

    const past = q('[data-cv2-pastilles]', grille);
    const pastN = neuf('[data-cv2-pastilles]');
    if (past && pastN) past.innerHTML = pastN.innerHTML;

    const nb = q('[data-cv2-nombre]', grille);
    const nbN = neuf('[data-cv2-nombre]');
    if (nb && nbN) nb.textContent = nbN.textContent;

    const voir = q('[data-cv2-voir]', grille);
    const voirN = neuf('[data-cv2-voir]');
    if (voir && voirN) voir.textContent = voirN.textContent;

    const eff = q('[data-cv2-effacer]', grille);
    const effN = neuf('[data-cv2-effacer]');
    if (eff && effN) {
      eff.setAttribute('href', effN.getAttribute('href'));
      eff.hidden = effN.hidden;
    }

    const filtres = q('[data-cv2-filtres]', grille);
    const filtresN = neuf('[data-cv2-filtres]');
    if (filtres && filtresN) {
      const ouverts = {};
      qa('details[data-cv2-groupe]', filtres).forEach((d) => {
        ouverts[d.dataset.cv2Groupe] = d.open;
      });
      const actif = document.activeElement;
      let cle = null;
      if (actif && filtres.contains(actif) && actif.name) cle = [actif.name, actif.value];
      else if (actif && filtres.contains(actif) && actif.closest('details')) cle = ['@', actif.closest('details').dataset.cv2Groupe];

      filtres.innerHTML = filtresN.innerHTML;

      qa('details[data-cv2-groupe]', filtres).forEach((d) => {
        if (d.dataset.cv2Groupe in ouverts) d.open = ouverts[d.dataset.cv2Groupe];
      });
      if (cle) {
        const cible =
          cle[0] === '@'
            ? q(`details[data-cv2-groupe="${CSS.escape(cle[1])}"] > summary`, filtres)
            : qa('input', filtres).find((i) => i.name === cle[0] && i.value === cle[1]);
        if (cible) cible.focus({ preventScroll: true });
      }
    }

    const zone = q('[data-cv2-nombre]', doc) || q('[data-cv2-voir]', doc);
    if (zone) annoncer(grille, zone.textContent.trim());
  }

  let jeton = 0;

  async function appliquer(grille, params, options = {}) {
    const base = grille.dataset.url;
    const chaine = params.toString();
    const url = `${base}?${chaine ? chaine + '&' : ''}section_id=${encodeURIComponent(grille.dataset.section)}`;
    const res = q('[data-cv2-resultats]', grille);
    const moi = ++jeton;
    if (res) res.classList.add('is-charge');
    try {
      const html = await charger(url);
      if (moi !== jeton) return;
      remplacer(grille, lire(html));
      grille.dataset.cv2Pages = '1';
      if (options.historique !== false) {
        history.pushState({ cv2: chaine }, '', base + (chaine ? '?' + chaine : ''));
      }
      document.dispatchEvent(new Event('products:refresh'));
    } catch (e) {
      /* Réseau ou réponse inattendue : navigation classique, rien ne se perd. */
      window.location.href = base + (chaine ? '?' + chaine : '');
    } finally {
      if (moi === jeton && res) res.classList.remove('is-charge');
    }
  }

  function tiroir(grille) {
    const dlg = q('[data-cv2-tiroir]', grille);
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const form = q('[data-cv2-form]', dlg);
    const corps = q('[data-cv2-corps]', dlg);

    const ouvrir = (cle) => {
      if (!dlg.open) {
        dlg.showModal();
        fige(true);
      }
      if (!cle) {
        corps.scrollTop = 0;
        return;
      }
      const groupe = q(`[data-cv2-groupe="${CSS.escape(cle)}"]`, dlg);
      if (!groupe) return;
      if (groupe.tagName === 'DETAILS') groupe.open = true;
      corps.scrollTop = Math.max(0, groupe.offsetTop - corps.offsetTop - 4);
      const cible = groupe.tagName === 'DETAILS' ? q('summary', groupe) : q('input', groupe);
      if (cible) cible.focus({ preventScroll: true });
    };

    grille.addEventListener('click', (e) => {
      const b = e.target.closest('[data-cv2-ouvrir]');
      if (b && grille.contains(b)) {
        e.preventDefault();
        ouvrir(b.dataset.cv2Ouvrir);
      }
    });

    dlg.addEventListener('click', (e) => {
      if (e.target === dlg || e.target.closest('[data-cv2-fermer]')) dlg.close();
    });
    dlg.addEventListener('close', () => fige(false));

    let minuteur;
    form.addEventListener('change', (e) => {
      if (!e.target.name) return;
      window.clearTimeout(minuteur);
      minuteur = window.setTimeout(() => appliquer(grille, paramsDu(form)), e.target.type === 'number' ? 600 : 200);
    });
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      window.clearTimeout(minuteur);
      const voulu = paramsDu(form).toString();
      if (voulu !== location.search.slice(1)) appliquer(grille, paramsDu(form));
      dlg.close();
    });

    /* Liens « Tout effacer » et pastilles de filtres actifs. */
    grille.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-cv2-lien-filtre]');
      if (!a || !grille.contains(a) || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      const url = new URL(a.getAttribute('href'), location.origin);
      url.searchParams.delete('page');
      synchroTri(grille, url.searchParams);
      appliquer(grille, url.searchParams);
    });
  }

  /* 5. « Voir plus » et retour arrière -------------------------------------- */

  const cleMemoire = () => 'cv2:' + location.pathname + location.search;

  function memoriser(grille) {
    const n = parseInt(grille.dataset.cv2Pages || '1', 10);
    try {
      if (n > 1) sessionStorage.setItem(cleMemoire(), JSON.stringify({ n, y: window.scrollY }));
      else sessionStorage.removeItem(cleMemoire());
    } catch (e) {
      /* stockage indisponible : on perd seulement la reprise */
    }
  }

  async function suite(grille, lien, focus) {
    const url = new URL(lien.getAttribute('href'), location.origin);
    url.searchParams.set('section_id', grille.dataset.section);
    lien.classList.add('is-charge');
    lien.setAttribute('aria-busy', 'true');
    try {
      const doc = lire(await charger(url.pathname + url.search));
      const liste = q('[data-cv2-liste]', grille);
      const nouveaux = qa('[data-cv2-liste] > [data-cv2-produit]', doc);
      const premier = nouveaux[0];
      nouveaux.forEach((li) => liste.appendChild(li));

      const zone = q('[data-cv2-plus-zone]', grille);
      const zoneN = q('[data-cv2-plus-zone]', doc);
      if (zone && zoneN) zone.replaceWith(zoneN);
      else if (zone) zone.remove();

      /* view_item_list du lot chargé (même format que la page). */
      const dl = q('[data-cv2-resultats] script[id^="Datalayer-Json"]', doc);
      if (dl && typeof window.sendDatalayer === 'function') {
        const boite = document.createElement('div');
        boite.appendChild(dl);
        window.sendDatalayer('view_item_list', boite);
      }

      grille.dataset.cv2Pages = String(parseInt(grille.dataset.cv2Pages || '1', 10) + 1);
      if (focus && premier) {
        const a = q('.cv2-carte__lien', premier);
        if (a) a.focus({ preventScroll: true });
      }
      return true;
    } catch (e) {
      window.location.href = lien.getAttribute('href');
      return false;
    }
  }

  function plus(grille) {
    grille.addEventListener('click', (e) => {
      const lien = e.target.closest('a[data-cv2-plus]');
      if (!lien || !grille.contains(lien) || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      suite(grille, lien, true);
    });

    window.addEventListener('pagehide', () => memoriser(grille));

    /* Retour depuis une fiche produit sans cache de page : les lots déjà vus
       sont rechargés, puis la position de lecture est rendue. */
    let m = null;
    try {
      m = JSON.parse(sessionStorage.getItem(cleMemoire()) || 'null');
    } catch (e) {
      m = null;
    }
    const nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || null;
    if (!m || !(m.n > 1) || !nav || nav.type !== 'back_forward') return;
    (async () => {
      for (let i = 1; i < m.n; i += 1) {
        const lien = q('a[data-cv2-plus]', grille);
        if (!lien || !(await suite(grille, lien, false))) break;
      }
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, m.y);
      document.documentElement.style.scrollBehavior = '';
    })();
  }

  /* 6. Ajout rapide ---------------------------------------------------------- */

  function upcart() {
    return typeof window.upcartOpenCart === 'function' || typeof window.upcartRefreshCart === 'function';
  }

  /* Même garde-fou que la landing MOVE : quand UpCart tient le tiroir, le
     thème ne doit pas ouvrir en plus le tiroir Dawn (cart-notification). */
  function gardeNotification() {
    const notif = document.querySelector('cart-notification');
    if (!notif || notif.dataset.cv2Garde) return;
    if (typeof notif.renderContents !== 'function') {
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('cart-notification').then(gardeNotification);
      }
      return;
    }
    notif.dataset.cv2Garde = '1';
    const original = notif.renderContents.bind(notif);
    notif.renderContents = function (etat) {
      if (!upcart()) return original(etat);
      try {
        if (!etat || !etat.sections) return undefined;
        this.cartItemKey = etat.key;
        this.getSectionsToRender().forEach((s) => {
          const hote = document.getElementById(s.id);
          const html = etat.sections[s.id];
          if (!hote || !html) return;
          const cible = (s.selector && hote.querySelector(s.selector)) || hote;
          cible.innerHTML = this.getSectionInnerHTML(html, s.selector);
        });
      } catch (e) {
        /* l'article est déjà dans le panier */
      }
      return undefined;
    };
  }

  /* UpCart intercepte l'envoi du formulaire de la feuille avant nous : il
     ajoute lui-même (/cart/add.js?upcart=1) et ouvre son tiroir, et notre
     écouteur `submit` ne reçoit rien (relevé sur le site le 29/09/2026). La
     feuille, modale, restait alors ouverte par-dessus le tiroir et le rendait
     inerte. On la ferme dès qu'UpCart annonce l'ajout ou ouvre son tiroir.
     Ses abonnements n'existent qu'une fois son script chargé (asynchrone). */
  let upcartEcoute = false;
  function ecouterUpcart() {
    if (upcartEcoute) return;
    const ouvert = window.upcartSubscribeCartOpened;
    const ajoute = window.upcartSubscribeAddedToCart;
    if (typeof ouvert !== 'function' && typeof ajoute !== 'function') return;
    upcartEcoute = true;
    const fermer = () => qa('[data-cv2-feuille][open]').forEach((d) => d.close());
    try {
      if (typeof ouvert === 'function') ouvert(fermer);
      if (typeof ajoute === 'function') ajoute(fermer);
    } catch (e) {
      /* rien */
    }
  }

  /* Envoi pris par UpCart : le bouton reste occupé le temps de sa réponse
     (un double clic ferait deux ajouts), puis se libère s'il y a eu une
     erreur et que la feuille est restée ouverte. */
  function envoiUpcart() {
    window.addEventListener(
      'submit',
      (e) => {
        const form = e.target && e.target.closest ? e.target.closest('[data-cv2-ajout-form]') : null;
        if (!form || !upcart()) return;
        ecouterUpcart();
        const bouton = q('[data-cv2-ajout-bouton]', form);
        if (!bouton) return;
        /* Après la lecture du formulaire par UpCart, qui se fait dans l'envoi. */
        setTimeout(() => {
          bouton.setAttribute('aria-busy', 'true');
          bouton.disabled = true;
          setTimeout(() => {
            bouton.removeAttribute('aria-busy');
            bouton.disabled = false;
          }, 4000);
        }, 0);
      },
      true
    );
  }

  function fragment(html) {
    const doc = lire(html);
    return (doc.querySelector('.shopify-section') || doc.body).innerHTML;
  }

  function apresAjout(item) {
    try {
      document.dispatchEvent(new CustomEvent('productAdded', { detail: { item } }));
    } catch (e) {
      /* rien */
    }
    if (upcart()) {
      try {
        const bulle = document.getElementById('cart-icon-bubble');
        if (bulle && item && item.sections && item.sections['cart-icon-bubble']) {
          bulle.innerHTML = fragment(item.sections['cart-icon-bubble']);
        }
      } catch (e) {
        /* rien */
      }
      try {
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
        if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
      } catch (e) {
        /* rien */
      }
    } else {
      const notif = document.querySelector('cart-notification');
      try {
        if (notif && typeof notif.renderContents === 'function' && item && item.sections) notif.renderContents(item);
      } catch (e) {
        /* rien */
      }
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {
      /* rien */
    }
  }

  function ressources(dlg) {
    if (dlg.dataset.cv2Ressources) return;
    dlg.dataset.cv2Ressources = '1';
    (dlg.dataset.css || '').split(' ').filter(Boolean).forEach((href) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      document.head.appendChild(l);
    });
    const js = dlg.dataset.jsGuide;
    if (js && !document.querySelector('script[src*="size-guide-html"]')) {
      const s = document.createElement('script');
      s.src = js;
      s.defer = true;
      document.body.appendChild(s);
    }
  }

  function formulaire(dlg, contenu) {
    const form = q('[data-cv2-ajout-form]', contenu);
    const json = q('[data-cv2-variantes]', contenu);
    if (!form || !json) return;
    let data = {};
    try {
      data = JSON.parse(json.textContent);
    } catch (e) {
      data = {};
    }
    const variantes = data.variantes || [];
    const options = qa('[data-cv2-option]', contenu);
    const champ = q('[data-cv2-ajout-id]', contenu);
    const bouton = q('[data-cv2-ajout-bouton]', contenu);
    const libelle = q('[data-cv2-ajout-libelle]', contenu);
    const prix = q('[data-cv2-ajout-prix]', contenu);
    const erreur = q('[data-cv2-ajout-erreur]', contenu);
    const image = q('[data-cv2-ajout-image] img', contenu);

    /* Taille déjà filtrée sur la collection : présélectionnée si unique. */
    const filtrees = [];
    new URLSearchParams(location.search).forEach((v, k) => {
      if (TAILLE.test(k)) filtrees.push(v);
    });
    if (filtrees.length === 1) {
      options.forEach((fs) => {
        if (q('input:checked', fs)) return;
        const r = qa('input[type="radio"]', fs).find((x) => x.value === filtrees[0]);
        if (r) r.checked = true;
      });
    }

    const choix = () => options.map((fs) => {
      const r = q('input[type="radio"]:checked', fs);
      return r ? r.value : null;
    });

    const peindre = () => {
      const sel = choix();
      options.forEach((fs, i) => {
        qa('input[type="radio"]', fs).forEach((r) => {
          const essai = sel.slice();
          essai[i] = r.value;
          const dispo = variantes.some((v) => v.dispo && v.options.every((o, j) => essai[j] == null || essai[j] === o));
          const lab = q(`label[for="${CSS.escape(r.id)}"]`, fs);
          if (lab) lab.classList.toggle('is-epuise', !dispo);
          r.setAttribute('aria-label', dispo ? r.value : `${r.value}, ${data.epuise || ''}`);
        });
        const val = q('[data-cv2-option-val]', fs);
        if (val) val.textContent = sel[i] || '';
      });

      /* Photo de la couleur choisie, même sans taille. */
      if (image) {
        const proche = variantes.find((v) => v.image && v.options.every((o, j) => sel[j] == null || sel[j] === o));
        if (proche && image.getAttribute('src') !== proche.image) {
          image.removeAttribute('srcset');
          image.setAttribute('src', proche.image);
        }
      }

      const complet = sel.every((x) => x != null);
      const v = complet ? variantes.find((x) => x.options.every((o, j) => sel[j] === o)) : null;
      champ.value = v ? String(v.id) : '';
      if (!v) {
        bouton.disabled = true;
        libelle.textContent = dlg.dataset.choisir || data.ajouter || '';
        return;
      }
      if (prix) prix.textContent = v.prixTexte;
      bouton.disabled = !v.dispo;
      libelle.textContent = v.dispo ? `${data.ajouter} · ${v.prixTexte}` : data.epuise;
    };

    form.addEventListener('change', () => {
      if (erreur) erreur.hidden = true;
      peindre();
    });
    peindre();

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (bouton.disabled || !champ.value) return;
      bouton.disabled = true;
      bouton.setAttribute('aria-busy', 'true');
      if (erreur) erreur.hidden = true;

      const corps = new FormData();
      corps.append('id', champ.value);
      corps.append('quantity', '1');
      const notif = document.querySelector('cart-notification');
      if (upcart()) {
        corps.append('sections', 'cart-icon-bubble');
        corps.append('sections_url', location.pathname);
      } else if (notif && typeof notif.getSectionsToRender === 'function') {
        corps.append('sections', notif.getSectionsToRender().map((s) => s.id).join(','));
        corps.append('sections_url', location.pathname);
      }
      const url = (window.routes && window.routes.cart_add_url) || '/cart/add';
      try {
        const r = await fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: corps,
        });
        const res = await r.json();
        if (res && res.status) {
          if (erreur) {
            erreur.textContent = res.description || res.message || dlg.dataset.erreur || '';
            erreur.hidden = false;
          }
          bouton.disabled = false;
          return;
        }
        libelle.textContent = dlg.dataset.ajoute || libelle.textContent;
        dlg.close();
        apresAjout(res);
      } catch (err) {
        if (erreur) {
          erreur.textContent = dlg.dataset.erreur || '';
          erreur.hidden = false;
        }
        bouton.disabled = false;
      } finally {
        bouton.removeAttribute('aria-busy');
      }
    });
  }

  function ajout(grille) {
    const dlg = q('[data-cv2-feuille]', grille);
    if (!dlg || typeof dlg.showModal !== 'function') return;
    const corps = q('[data-cv2-feuille-corps]', dlg);
    let guide = false;

    dlg.addEventListener('click', (e) => {
      if (e.target === dlg || e.target.closest('[data-cv2-fermer]')) {
        dlg.close();
        return;
      }
      /* Guide des tailles : modale du thème, hors de la couche du dialogue.
         La feuille se ferme le temps du guide et revient ensuite. */
      const b = e.target.closest('[data-cv2-guide]');
      if (!b) return;
      const m = document.getElementById(b.dataset.cv2Guide);
      if (!m) return;
      guide = true;
      dlg.close();
      if (typeof m.show === 'function') m.show(b);
      else m.setAttribute('open', '');
      const obs = new MutationObserver(() => {
        if (m.hasAttribute('open')) return;
        obs.disconnect();
        guide = false;
        dlg.showModal();
        fige(true);
        b.focus({ preventScroll: true });
      });
      obs.observe(m, { attributes: true, attributeFilter: ['open'] });
    });
    dlg.addEventListener('close', () => {
      if (!guide) fige(false);
    });

    grille.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-cv2-ajout]');
      if (!btn || !grille.contains(btn) || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      ressources(dlg);
      gardeNotification();
      ecouterUpcart();
      corps.replaceChildren();
      corps.setAttribute('aria-busy', 'true');
      dlg.showModal();
      fige(true);

      const url = new URL(btn.dataset.cv2Ajout, location.origin);
      url.searchParams.set('section_id', 'cv2-ajout');
      try {
        const doc = lire(await charger(url.pathname + url.search));
        const contenu = q('[data-cv2-ajout-contenu]', doc);
        if (!contenu) throw new Error('contenu absent');
        /* Une seule modale de guide par produit dans <body>. */
        qa('modal-dialog[id]', contenu).forEach((md) => {
          const ancien = document.getElementById(md.id);
          if (ancien) ancien.remove();
        });
        if (contenu.dataset.pack === 'true') {
          const tpl = q('template[data-cv2-feuille-pack]', dlg);
          const ici = q('[data-cv2-ajout-pack-ici]', contenu);
          if (tpl && ici) ici.replaceWith(tpl.content.cloneNode(true));
        }
        const fiche = q('[data-cv2-ajout-fiche]', contenu);
        const tplF = q('template[data-cv2-feuille-fiche]', dlg);
        if (fiche && tplF && tplF.content.textContent.trim()) fiche.textContent = tplF.content.textContent.trim();
        /* Couleur choisie sur la carte : présélectionnée dans la feuille. */
        const couleur = btn.dataset.cv2Couleur;
        if (couleur) {
          const r = qa('.cv2-ajout__option--couleur input[type="radio"]', contenu).find((x) => x.value === couleur);
          if (r) r.checked = true;
        }
        corps.replaceChildren(document.adoptNode(contenu));
        corps.removeAttribute('aria-busy');
        formulaire(dlg, contenu);
        const premier = q('input[type="radio"]:not(:checked)', contenu) || q('button, a', contenu);
        if (premier) premier.focus({ preventScroll: true });
      } catch (err) {
        window.location.href = btn.dataset.cv2Ajout;
      }
    });
  }

  /* 7. Bande packs et galeries --------------------------------------------- */

  /* Écoute déléguée au document : la bande est dans la grille, que les filtres
     et le tri remplacent sans recharger la page. Des écouteurs posés sur les
     onglets au chargement ne répondaient plus après un filtre (28/09). */
  function onglets() {
    const choisir = (t, focus) => {
      const bande = t.closest('[data-cv2-packs]');
      if (!bande) return;
      qa('[data-cv2-onglet]', bande).forEach((x) => {
        const on = x === t;
        x.setAttribute('aria-selected', String(on));
        x.tabIndex = on ? 0 : -1;
        const p = document.getElementById(x.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      if (focus) t.focus();
    };
    document.addEventListener('click', (e) => {
      const t = e.target.closest && e.target.closest('[data-cv2-onglet]');
      if (t) choisir(t, false);
    });
    document.addEventListener('keydown', (e) => {
      const pas = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      const t = pas && e.target.closest && e.target.closest('[data-cv2-onglet]');
      if (!t) return;
      const tabs = qa('[data-cv2-onglet]', t.closest('[data-cv2-packs]') || document);
      const i = tabs.indexOf(t);
      e.preventDefault();
      choisir(tabs[(i + pas + tabs.length) % tabs.length], true);
    });
  }

  /* Galeries des cartes : photo par photo, points, flèches au survol (souris)
     et boucle. Le script ajoute en fin de galerie une copie de la première
     photo ; arrivée sur cette copie, la galerie revient sans animation à la
     vraie première, identique : glisser après la dernière photo ramène à la
     première au lieu de buter (et d'inviter au clic vers la fiche). La flèche
     « précédente » boucle aussi. Sans JS : galerie simple, sans copie. */

  const reelles = (g) => [...g.children].filter((el) => !el.hasAttribute('data-cv2-copie'));
  const rang = (g) => Math.round(g.scrollLeft / Math.max(g.clientWidth, 1));

  function boucle(g) {
    qa('[data-cv2-copie]', g).forEach((c) => c.remove());
    g.dataset.cv2Boucle = '1';
    const r = reelles(g);
    if (r.length < 2) return;
    const c = r[0].cloneNode(true);
    c.setAttribute('data-cv2-copie', '');
    c.setAttribute('alt', '');
    c.setAttribute('loading', 'lazy');
    c.removeAttribute('fetchpriority');
    g.appendChild(c);
  }

  const prete = (g) => {
    if (!g.dataset.cv2Boucle) boucle(g);
  };

  function majPoints(g) {
    const n = Math.max(reelles(g).length, 1);
    const i = rang(g) % n;
    qa('.cv2-carte__points i', g.parentElement).forEach((p, k) => p.classList.toggle('is-on', k === i));
  }

  /* Sur la copie : retour à la vraie première photo. Sauf pendant le saut de
     la flèche « précédente » (copie → dernière photo), verrouillé par
     data-cv2-saut jusqu'à ce que la galerie ait quitté la copie. */
  function recaler(g) {
    const n = reelles(g).length;
    if (n < 2) return;
    if (rang(g) < n) {
      delete g.dataset.cv2Saut;
      return;
    }
    if (g.dataset.cv2Saut) return;
    g.scrollLeft = 0;
    majPoints(g);
  }

  function galeries() {
    let attente = false;
    const finNative = 'onscrollend' in window;
    const minuteurs = new WeakMap();

    document.addEventListener(
      'scroll',
      (e) => {
        const g = e.target;
        if (!(g instanceof Element) || !g.hasAttribute('data-cv2-galerie')) return;
        prete(g);
        if (!finNative) {
          clearTimeout(minuteurs.get(g));
          minuteurs.set(g, setTimeout(() => recaler(g), 160));
        }
        if (attente) return;
        attente = true;
        window.requestAnimationFrame(() => {
          attente = false;
          majPoints(g);
        });
      },
      true
    );

    if (finNative) {
      document.addEventListener(
        'scrollend',
        (e) => {
          const g = e.target;
          if (g instanceof Element && g.hasAttribute('data-cv2-galerie')) recaler(g);
        },
        true
      );
    }

    /* Doigt posé sur une galerie : la copie est en place avant la fin. */
    document.addEventListener(
      'touchstart',
      (e) => {
        const g = e.target.closest && e.target.closest('[data-cv2-galerie]');
        if (g) prete(g);
      },
      { passive: true }
    );

    /* Flèches (souris) : une photo à la fois, vers une photo précise (un
       scrollBy pendant une animation en cours peut rester sur place). */
    document.addEventListener('click', (e) => {
      const f = e.target.closest && e.target.closest('[data-cv2-fleche]');
      if (!f) return;
      e.preventDefault();
      const g = q('[data-cv2-galerie]', f.closest('.cv2-carte__media'));
      if (!g) return;
      prete(g);
      const n = reelles(g).length;
      if (n < 2) return;
      const w = Math.max(g.clientWidth, 1);
      const i = rang(g) % n;
      if (Number(f.dataset.cv2Fleche) > 0) {
        /* Après la dernière : la copie, puis recaler() ramène à la première. */
        g.scrollTo({ left: (i + 1) * w, behavior: 'smooth' });
      } else if (i === 0) {
        /* Avant la première : saut sur la copie (même photo), puis la dernière.
           Le verrou tombe à l'arrivée, ou au plus tard après une seconde. */
        g.dataset.cv2Saut = '1';
        g.scrollLeft = n * w;
        window.requestAnimationFrame(() => g.scrollTo({ left: (n - 1) * w, behavior: 'smooth' }));
        setTimeout(() => {
          delete g.dataset.cv2Saut;
          recaler(g);
        }, 1000);
      } else {
        g.scrollTo({ left: (i - 1) * w, behavior: 'smooth' });
      }
    });
  }

  /* Pastilles de couleur des cartes ----------------------------------------
     Un clic montre les photos de la couleur (suite lue dans le JSON du
     produit, chargé une fois), fait pointer les liens de la carte sur cette
     couleur (?variant=) et la présélectionne dans l'ajout rapide. */

  const produits = new Map();
  function produitJson(url) {
    const cle = (url || '').split('?')[0];
    if (!cle) return Promise.reject(new Error('url'));
    if (!produits.has(cle)) {
      produits.set(
        cle,
        fetch(cle + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
      );
      produits.get(cle).catch(() => produits.delete(cle));
    }
    return produits.get(cle);
  }

  function largeur(src, w) {
    try {
      const u = new URL(src.indexOf('//') === 0 ? 'https:' + src : src, location.origin);
      u.searchParams.set('width', String(w));
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  function photo(src, alt, sizes, premiere) {
    const img = document.createElement('img');
    img.className = 'cv2-carte__img';
    img.alt = alt || '';
    img.decoding = 'async';
    img.loading = premiere ? 'eager' : 'lazy';
    img.src = largeur(src, 600);
    img.srcset = [300, 450, 600, 750, 900].map((w) => `${largeur(src, w)} ${w}w`).join(', ');
    if (sizes) img.sizes = sizes;
    return img;
  }

  function points(carte, n) {
    const zone = q('.cv2-carte__points', carte);
    if (zone) {
      zone.replaceChildren();
      if (n > 1) {
        for (let i = 0; i < n; i += 1) {
          const p = document.createElement('i');
          if (i === 0) p.className = 'is-on';
          zone.appendChild(p);
        }
      }
    }
    carte.classList.toggle('is-une-photo', n < 2);
  }

  /* Même règle que le Liquid de la carte : de la photo rattachée à la couleur
     jusqu'à celle de la couleur suivante, la 2e d'abord si demandé. */
  function suiteCouleur(produit, cpos, valeur, portee) {
    const media = (produit.media || []).filter((m) => m.media_type === 'image');
    const bornes = new Set();
    (produit.variants || []).forEach((v) => {
      if (v.featured_media) bornes.add(v.featured_media.position);
    });
    const v = (produit.variants || []).find((x) => x.featured_media && (x.options || [])[cpos] === valeur);
    if (!v) return [];
    const debut = v.featured_media.position;
    const apres = [...bornes].filter((p) => p > debut).sort((a, b) => a - b);
    const fin = apres.length ? apres[0] : Infinity;
    const liste = media.filter((m) => m.position >= debut && m.position < fin);
    if (portee && liste.length > 1) {
      const deux = liste[1];
      liste[1] = liste[0];
      liste[0] = deux;
    }
    return liste;
  }

  const nomFichier = (src) => (src || '').split('?')[0].split('/').pop();

  async function teinte(btn) {
    const carte = btn.closest('[data-cv2-carte]');
    if (!carte) return;
    qa('.cv2-teinte', carte).forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));

    const id = btn.dataset.cv2Variante;
    qa('a.cv2-carte__lien, a.cv2-carte__galerie', carte).forEach((a) => {
      const u = new URL(a.getAttribute('href'), location.origin);
      if (id) u.searchParams.set('variant', id);
      a.setAttribute('href', u.pathname + u.search);
    });
    const ajouter = q('[data-cv2-ajout]', carte);
    if (ajouter) ajouter.dataset.cv2Couleur = btn.dataset.cv2Couleur || '';

    const g = q('[data-cv2-galerie]', carte);
    if (!g) return;
    const modele = q('img', g);
    const sizes = modele ? modele.getAttribute('sizes') : '';
    const alt = modele ? modele.getAttribute('alt') : '';
    const jeton = String(Date.now() + Math.random());
    g.dataset.cv2Jeton = jeton;

    let premiere = null;
    if (btn.dataset.cv2Image) {
      premiere = photo(btn.dataset.cv2Image, alt, sizes, true);
      g.replaceChildren(premiere);
      g.scrollLeft = 0;
      points(carte, 1);
      boucle(g);
    }
    try {
      const produit = await produitJson(carte.dataset.cv2Produit);
      if (g.dataset.cv2Jeton !== jeton) return;
      const max = parseInt(carte.dataset.cv2Max || '12', 10);
      const liste = suiteCouleur(produit, parseInt(carte.dataset.cv2Cpos || '0', 10), btn.dataset.cv2Couleur, carte.dataset.cv2Portee === 'true').slice(0, max);
      if (!liste.length) return;
      const imgs = liste.map((m, i) => {
        if (i === 0 && premiere && nomFichier(premiere.getAttribute('src')) === nomFichier(largeur(m.src, 600))) return premiere;
        return photo(m.src, alt, sizes, i === 0);
      });
      g.replaceChildren(...imgs);
      g.scrollLeft = 0;
      points(carte, imgs.length);
      boucle(g);
    } catch (e) {
      /* la photo de la couleur reste affichée */
    }
  }

  function teintes() {
    document.addEventListener('click', (e) => {
      const b = e.target.closest && e.target.closest('.cv2-teinte');
      if (!b) return;
      e.preventDefault();
      teinte(b);
    });
    /* JSON du produit demandé dès que le doigt ou la souris approche des pastilles. */
    const prechauffer = (e) => {
      const zone = e.target.closest && e.target.closest('.cv2-carte__couleurs');
      if (!zone || zone.dataset.cv2Chaud) return;
      zone.dataset.cv2Chaud = '1';
      const carte = zone.closest('[data-cv2-carte]');
      if (carte) produitJson(carte.dataset.cv2Produit).catch(() => {});
    };
    document.addEventListener('pointerover', prechauffer, { passive: true });
    document.addEventListener('touchstart', prechauffer, { passive: true });
  }

  /* Démarrage ------------------------------------------------------------- */

  function initGrille(grille) {
    if (grille.dataset.cv2Pret) return;
    grille.dataset.cv2Pret = '1';
    barre(grille);
    tiroir(grille);
    plus(grille);
    ajout(grille);
  }

  function init(root) {
    plis(root);
    entete(root);
    qa('[data-cv2-grille]', root).forEach(initGrille);
  }

  let galeriesPretes = false;
  function demarrer() {
    init(document);
    if (!galeriesPretes) {
      galeriesPretes = true;
      galeries();
      teintes();
      onglets();
      envoiUpcart();
      ecouterUpcart();
      window.addEventListener('load', ecouterUpcart);
      [3000, 8000].forEach((ms) => setTimeout(ecouterUpcart, ms));
    }
    if (!history.state || typeof history.state.cv2 !== 'string') {
      history.replaceState({ ...(history.state || {}), cv2: location.search.slice(1) }, '');
    }
  }

  window.addEventListener('popstate', (e) => {
    if (!e.state || typeof e.state.cv2 !== 'string') return;
    const grille = q('[data-cv2-grille]');
    if (!grille) return;
    const params = new URLSearchParams(e.state.cv2);
    synchroTri(grille, params);
    appliquer(grille, params, { historique: false });
  });

  document.addEventListener('shopify:section:load', (e) => init(e.target));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})();
